import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, Mail, Trash2, AlertTriangle, XCircle } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

export default function SettingsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [allowUserCalls, setAllowUserCalls] = useState(true)
  const [loading, setLoading] = useState(true)
  const [scheduledDeletion, setScheduledDeletion] = useState<{
    scheduledAt: string
    reason: string
    daysRemaining: number
  } | null>(null)

  useEffect(() => {
    loadPreferences()
  }, [])

  const loadPreferences = async () => {
    try {
      setLoading(true)
      // TODO: Load preferences from API
      // const response = await api.getUserPreferences(token)
      // setAllowUserCalls(response.preferences.allowUserCalls)
      // setScheduledDeletion(response.scheduledDeletion)
    } catch (error) {
      console.error('Error loading preferences:', error)
    } finally {
      setLoading(false)
    }
  }

  const updatePreference = async (field: string, value: boolean) => {
    try {
      // TODO: Update preference via API
      console.log(`Updating ${field} to:`, value)
    } catch (error) {
      console.error(`Error updating ${field}:`, error)
      // Revert on error
      if (field === 'allowUserCalls') {
        setAllowUserCalls(!value)
      }
    }
  }

  const handleDeleteAccount = () => {
    if (scheduledDeletion) {
      // Show cancel deletion option
      if (window.confirm(`Your account is scheduled for deletion in ${scheduledDeletion.daysRemaining} days. Would you like to cancel the deletion?`)) {
        cancelAccountDeletion()
      }
    } else {
      // Navigate to delete account page
      navigate('/delete-account')
    }
  }

  const cancelAccountDeletion = async () => {
    try {
      // TODO: Cancel account deletion via API
      console.log('Cancelling account deletion')
      setScheduledDeletion(null)
      alert('Account deletion has been cancelled.')
    } catch (error) {
      console.error('Error cancelling account deletion:', error)
      alert('Failed to cancel account deletion')
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-600">Loading preferences...</p>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-3xl font-poppins font-bold text-gray-900 mb-6">Settings</h1>

      {scheduledDeletion && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 flex items-start">
          <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="ml-3 text-sm text-red-700">
            Account deletion scheduled in {scheduledDeletion.daysRemaining} days
          </p>
        </div>
      )}

      {/* Privacy Section */}
      <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Privacy</h2>
        </div>

        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <Phone className="h-6 w-6 text-gray-700 flex-shrink-0" />
              <span className="ml-3 text-base text-gray-900">Allow Calls</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={allowUserCalls}
                onChange={(e) => {
                  setAllowUserCalls(e.target.checked)
                  updatePreference('allowUserCalls', e.target.checked)
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Account Section */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Account</h2>
        </div>

        <div className="divide-y divide-gray-100">
          {/* Email Info */}
          <div className="px-6 py-4">
            <div className="flex items-center">
              <Mail className="h-6 w-6 text-gray-700 flex-shrink-0" />
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">Email</p>
                <p className="text-sm text-gray-600 mt-0.5">{user?.email || 'No email provided'}</p>
              </div>
            </div>
          </div>

          {/* Change Password (Disabled for now) 
          <button
            onClick={() => navigate('/change-password')}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center">
              <Lock className="h-6 w-6 text-gray-700" />
              <span className="ml-3 text-base text-gray-900">Change Password</span>
            </div>
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          */}

          {/* Delete Account */}
          <button
            onClick={handleDeleteAccount}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-red-50 transition-colors group"
          >
            <div className="flex items-center">
              {scheduledDeletion ? (
                <XCircle className="h-6 w-6 text-red-600" />
              ) : (
                <Trash2 className="h-6 w-6 text-red-600" />
              )}
              <span className="ml-3 text-base text-red-600 font-medium">
                {scheduledDeletion ? 'Cancel Account Deletion' : 'Delete Account'}
              </span>
            </div>
            <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
